import { Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { useWishlist } from "@/contexts/WishlistContext";
import { DiscountPrice } from "./DiscountPrice";
import { isDiscountActive } from "@/lib/discount-utils";
import { optimizeSupabaseImage, getOriginalUrl, IMAGE_SIZES } from "@/utils/imageOptimization";
import { getDisplayPriceCents } from "@/lib/product-pricing";
import { DigitalBadge } from "./DigitalBadge";

interface MobileProductCardProps {
  product: {
    id: string;
    title: string;
    price_cents: number;
    currency: string;
    main_image_url?: string;
    discount_percentage?: number;
    discounted_price_cents?: number;
    discount_start_date?: string;
    discount_end_date?: string;
    has_variants?: boolean;
    variants?: any;
    is_digital?: boolean;
  };
  loadEager?: boolean;
}

export function MobileProductCard({ product, loadEager = false }: MobileProductCardProps) {
  const { isInWishlist, toggleWishlist } = useWishlist();
  const isWishlisted = isInWishlist(product.id);

  const discountInfo = {
    discount_percentage: product.discount_percentage,
    discount_start_date: product.discount_start_date,
    discount_end_date: product.discount_end_date,
  };

  const handleWishlistClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product.id);
  };

  return (
    <Link to={`/product/${product.id}`} className="group block">
      <div className="relative">
        {/* Product Image */}
        <div className="relative aspect-[4/5] overflow-hidden rounded-lg bg-muted">
          {product.is_digital && <DigitalBadge />}
          {product.main_image_url ? (
            <>
              <img
                src={optimizeSupabaseImage(product.main_image_url, IMAGE_SIZES.productCardMobile)}
                alt={product.title}
                width="320"
                height="400"
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading={loadEager ? "eager" : "lazy"}
                fetchPriority={loadEager ? "high" : "auto"}
                onError={(e) => {
                  const originalUrl = getOriginalUrl(product.main_image_url);
                  if (e.currentTarget.src !== originalUrl) {
                    e.currentTarget.src = originalUrl;
                  } else {
                    e.currentTarget.style.display = 'none';
                    const fallbackDiv = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fallbackDiv) fallbackDiv.style.display = 'flex';
                  }
                }}
              />
              <div className="hidden h-full w-full items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5">
                <div className="text-4xl opacity-20">🎨</div>
              </div>
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5">
              <div className="text-4xl opacity-20">🎨</div>
            </div>
          )}
          
          {/* Wishlist Button */}
          <button
            onClick={handleWishlistClick}
            className="absolute top-2 right-2 p-2 bg-white/90 rounded-full shadow-sm hover:bg-white perlla-transition"
          >
            <Heart 
              className={`h-4 w-4 ${
                isWishlisted 
                  ? "fill-primary text-primary" 
                  : "text-muted-foreground"
              }`} 
            />
          </button>

          {/* Discount Badge */}
          {isDiscountActive(discountInfo) && (
            <div className="absolute top-2 left-2 bg-secondary text-secondary-foreground px-2 py-1 rounded text-xs font-medium">
              -{product.discount_percentage}%
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="mt-2 space-y-1 min-h-[3rem]">
          <h3 className="text-sm font-medium text-card-foreground line-clamp-2 group-hover:text-primary perlla-transition leading-tight">
            {product.title}
          </h3>
          
          <div className="flex items-center gap-1">
            {product.has_variants && <span className="text-xs text-muted-foreground">From</span>}
            <DiscountPrice
              originalPriceCents={getDisplayPriceCents(product)}
              currency={product.currency}
              discountInfo={{
                discount_percentage: product.discount_percentage,
                discount_start_date: product.discount_start_date,
                discount_end_date: product.discount_end_date,
                discounted_price_cents: product.discounted_price_cents,
              }}
              className="text-sm"
              showBadge={false}
            />
          </div>
        </div>
      </div>
    </Link>
  );
}